<script>
  import {
    Button,
    SimpleDataTable,
    DataCell,
    Icon,
    Text,
    Buttons,
    Badge,
    Page,
    PageHeader,
  } from "@gl/design-system";
  import { _, user } from "../../stores/index";
  import { navigate } from "svelte-routing";
  import { onMount } from "svelte";
  import { AuthorizersRepository } from "../../repositories";
  import {
    faPen,
    faTrashAlt,
    faPlus,
  } from "@fortawesome/pro-regular-svg-icons";
  import { ConfirmModal, AuthorizerModal } from "../../components/index";
  import Layout from "../../components/Layout.svelte";

  let loading = {};
  let authorizers = [];

  let modal = false;
  let confirmModal = false;
  let selectedAuthorizer;

  async function fetchAuthorizers() {
    loading.authorizers = true;
    try {
      const result = await AuthorizersRepository.getAuthorizers();
      authorizers = result.list;
    } finally {
      loading.authorizers = false;
    }
  }

  async function handleDelete() {
    loading.delete = true;
    try {
      await AuthorizersRepository.deleteAuthorizer(selectedAuthorizer.id);
    } finally {
      fetchAuthorizers();
      loading.delete = false;
    }
  }

  onMount(async () => {
    if (!$user.data.isMaster) {
      navigate("/forbidden");
      return;
    }
    await fetchAuthorizers();
  });
</script>

{#if modal}
  <AuthorizerModal
    {selectedAuthorizer}
    bind:active={modal}
    on:close={() => {
      modal = false;
      selectedAuthorizer = null;
    }}
    on:success={() => {
      fetchAuthorizers();
      modal = false;
      selectedAuthorizer = null;
    }}
  />
{/if}

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false;
      selectedAuthorizer = null;
    }}
    handleYes={handleDelete}
    title={$_("delete.resource", {
      values: { resourceType: $_("common.authorizer") },
    })}
    confirmationValue={selectedAuthorizer?.data?.name}
  >
    <p>
      {@html $_("delete.warning.access2", {
        values: { resourceName: `${selectedAuthorizer?.data?.name} ` },
      })}
    </p>

    <p class="mt-4 mb-2">
      {@html $_("delete.confirmation", {
        values: {
          resourceType: $_("common.authorizer").toLowerCase(),
          resourceId: selectedAuthorizer?.data?.name,
        },
      })}
    </p>
  </ConfirmModal>
{/if}

<Layout selected="authorizers">
  <Page
    title={`${$_("admin.title")} | ${$_("common.authorizers")} | ${$_("access-manager")}`}
  >
    <svelte:fragment slot="header">
      <PageHeader heading={$_("common.authorizers")}>
        <Button
          slot="actions"
          on:click={() => {
            selectedAuthorizer = null;
            modal = true;
          }}
        >
          <Icon icon={faPlus} />
          <Text>{$_("authorizers.new")}</Text>
        </Button>
      </PageHeader>
    </svelte:fragment>

    <SimpleDataTable
      let:index
      let:row
      data={authorizers}
      fullWidth
      selectable={false}
      variant="separate"
      loading={loading.authorizers}
      stickyHead
    >
      <DataCell {index} id={`${row.id}-id`} header="ID">
        {row.id}
      </DataCell>

      <DataCell {index} header={$_("common.name")} id={`${row.id}-name`}>
        {row.data.name}
      </DataCell>

      <DataCell
        {index}
        header={$_("authorizers.issuer")}
        id={`${row.id}-issuer`}
      >
        {row.data.issuer}
      </DataCell>

      <DataCell {index} header={$_("common.type")} id={`${row.id}-type`}>
        {row.data.type}
      </DataCell>

      <DataCell
        {index}
        header={$_("authorizers.externalUserManagement")}
        id={`${row.id}-ext`}
      >
        {row.data.externalUserManagement}
      </DataCell>

      <DataCell
        {index}
        header={$_("authorizers.emailDomain")}
        id={`${row.id}-domain`}
      >
        {row.data.emailDomain ?? "-"}
      </DataCell>

      <DataCell
        {index}
        header={$_("authorizers.jwksUri")}
        id={`${row.id}-jwks`}
      >
        {row.data.jwksUri}
      </DataCell>

      <DataCell {index} header="">
        <Buttons>
          <Button
            size="tiny"
            on:click={() => {
              selectedAuthorizer = row;
              modal = true;
            }}
          >
            <Icon icon={faPen} />
            <Text>{$_("buttons.edit")}</Text>
          </Button>
          <Button
            outlined
            size="tiny"
            status="error"
            on:click={() => {
              selectedAuthorizer = row;
              confirmModal = true;
            }}
          >
            <Icon icon={faTrashAlt} />
            <Text>{$_("buttons.delete")}</Text>
          </Button>
        </Buttons>
      </DataCell>
    </SimpleDataTable>
  </Page>
</Layout>
